import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import AnimatedWords from '../animations/AnimatedWords';
import ContactForm from './ContactForm';
import corporateContentData from '../../content/corporateContentData';

// Default transition constants for reuse
const defaultParagraphTransition = { duration: 0.5, ease: 'easeOut' };
const defaultHeadingTransition = { duration: 0.5, ease: 'easeOut' };
const defaultListTransition = { duration: 0.5, ease: 'easeOut' };
const defaultItemTransition = { duration: 0.5, ease: 'easeOut' };

const createListVariants = (delay = 0) => ({
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      ...defaultListTransition,
      delayChildren: delay,
      staggerChildren: 0.12
    }
  }
});

const CorporateContainer = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 80px 40px 40px;
  transition: all 0.5s ease-in-out;

  @media (max-width: 768px) {
    padding: 60px 20px 20px;
  }
`;

const HeroSection = styled.section`
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 0;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 80px 0 40px;
  }
`;

const CorporateHeading = styled.h1`
  font-family: 'Naveid Arabic DEMO', 'Generic-G80-RD-Standard-DEMO', -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 3rem;
  font-weight: 300;
  color: #333;
  text-align: center;
  letter-spacing: 0.05em;
  font-style: normal;
  font-weight: 700;
  font-size: 60px;
  line-height: 60px;
  text-transform: uppercase;

  max-width: 1200px;
  margin: 0 auto;
  user-select: none;
  -webkit-user-select: none;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-top: 0;
    margin-bottom: 0;
  }
`;


const ContentSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
  max-width: 1200px;
  width: 100%;
`;

const ContentSection = styled.div`
  display: flex;
  min-height: auto;
  position: relative;
  justify-content: space-between;
  gap: 2rem;
  padding: 160px 0;

  @media (max-width: 768px) {
    flex-direction: column;
    min-height: auto;
    gap: 2rem;
    padding: 80px 0;
  }
`;


const SectionHeading = styled.h2`
  position: sticky;
  top: 0;
  font-family: 'Naveid Arabic DEMO', 'Generic-G80-RD-Standard-DEMO', -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.2;
  width: 300px;
  padding: 2.4rem 0 0 0;
  margin: 0;
  z-index: 10;
  align-self: flex-start;
  font-style: normal;
  font-weight: 700;
  font-size: 40px;
  line-height: 45px;
  letter-spacing: 0.02em;
  text-transform: uppercase;


  @media (max-width: 768px) {
    font-size: 1.8rem;
    width: 100%;
    padding: 0;
    position: static;
    margin-bottom: 1rem;
  }
`;

const SectionContent = styled.div`
  flex: 1;
  padding: 2rem 0 0 2rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  max-width: 840px;
  align-items: end;

  @media (max-width: 768px) {
    padding: 0;
    max-width: 100%;
  }
`;

const ContentText = styled(motion.div)`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  color: #000000;
  margin: 0;
`;
ContentText.defaultProps = {
  initial: "hidden",
  whileInView: "visible",
  viewport: { once: true, amount: 0.1 },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0
      }
    }
  }
};

const ContentParagraph = styled(motion.p)`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  color: #000000;
  margin: 0 0 1.5rem 0;
`;
ContentParagraph.defaultProps = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: defaultParagraphTransition
    }
  }
};

const ContentHeading = styled(motion.h3)`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #000000;
  margin: 2rem 0 1rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;
ContentHeading.defaultProps = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: defaultHeadingTransition
    }
  }
};

const ServiceList = styled(motion.ul)`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  color: #000000;
  margin: 0 0 1.5rem 0;
  padding-left: 0;
  list-style: none;
`;
ServiceList.defaultProps = {
  initial: "hidden",
  whileInView: "visible",
  viewport: { once: true, amount: 0.2 },
  variants: createListVariants()
};

const ServiceItem = styled(motion.li)`
  margin: 0.5rem 0;
  position: relative;
  padding-left: 1rem;
  
  &:before {
    content: "•";
    position: absolute;
    left: 0;
    color: #000000;
  }
`;
ServiceItem.defaultProps = {
  variants: {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: defaultItemTransition
    }
  }
};

const NumberedList = styled(motion.ol)`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  color: #000000;
  margin: 0 0 1.5rem 0;
  padding-left: 0;
  list-style: none;
  counter-reset: item;
`;
NumberedList.defaultProps = {
  initial: "hidden",
  whileInView: "visible",
  viewport: { once: true, amount: 0.1 },
  variants: createListVariants()
};

const NumberedItem = styled(motion.li)`
  margin: 1.5rem 0;
  position: relative;
  padding-left: 2rem;
  counter-increment: item;
  
  &:before {
    content: counter(item) ".";
    position: absolute;
    left: 0;
    color: #000000;
    font-weight: 600;
  }
`;
NumberedItem.defaultProps = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: defaultItemTransition
    }
  }
};

const NumberedHeading = styled(motion.h4)`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 26px;
  color: #000000;
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const NumberedDescription = styled(motion.div)`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  color: #000000;
  margin: 0 0 0.5rem 0;
`;

const CorporateFooter = styled.footer`
  width: 100%;
  max-width: 1200px;
  margin-top: 4rem;
  padding: 2rem 0;
  text-align: center;

  @media (max-width: 768px) {
    margin-top: 2rem;
    padding: 1.5rem 0;
  }
`;

const FooterText = styled.p`
  font-family: 'Chivo Mono';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #666;
  margin: 0;
`;


const createHeadingVariants = (delay) => ({
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 60,
      damping: 20,
      duration: 0.8,
      delay
    }
  }
});

const createContentDelayVariants = (delay) => ({
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: delay
    }
  }
});

const renderHTML = (html) => ({ __html: html });

const CorporateContent = () => {
  const headingText = corporateContentData.heroHeading;
  const [headingAnimationComplete, setHeadingAnimationComplete] = React.useState(false);
  const headingAnimationStartedRef = React.useRef(false);

  // Calculate heading animation duration
  // delayChildren: 0.1s + staggerChildren: 0.04s * (wordCount - 1) + wordDuration: 0.8s
  const wordCount = headingText.split(' ').length;
  const headingAnimationDuration = 0.1 + (0.04 * (wordCount - 1)) + 0.8;
  const headingAnimationDelayCap = 1.6;
  const headingAnimationGateDuration = Math.min(headingAnimationDuration, headingAnimationDelayCap);

  React.useEffect(() => {
    // Only start the timer once when component first mounts
    if (!headingAnimationStartedRef.current) {
      headingAnimationStartedRef.current = true;
      // Mark heading animation as complete after calculated duration
      const timer = setTimeout(() => {
        setHeadingAnimationComplete(true);
      }, headingAnimationGateDuration * 1000);

      return () => clearTimeout(timer);
    }
  }, [headingAnimationDuration, headingAnimationGateDuration]);

  return (
    <CorporateContainer>
      <HeroSection>
        <AnimatedWords 
          as={CorporateHeading}
          key="corporate-heading" // Prevent re-animation on remount
        >
          {headingText}
        </AnimatedWords>
      </HeroSection>
      <ContentSections>
        {corporateContentData.sections.map((section) => {
          if (section.type === 'values') {
            return (
              <ContentSection key={section.id}>
                <AnimatedWords
                  as={SectionHeading}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.1 }}
                  variants={createHeadingVariants(headingAnimationComplete ? 0 : headingAnimationGateDuration)}
                >
                  {section.title}
                </AnimatedWords>
                <SectionContent>
                  <ContentText>
                    <NumberedList>
                      {section.items.map((item, index) => (
                        <NumberedItem
                          key={`${item.title}-${index}`}
                          transition={{ ...defaultItemTransition, delay: index * 0.1 }}
                        >
                          <NumberedHeading>{item.title}</NumberedHeading>
                          <NumberedDescription dangerouslySetInnerHTML={renderHTML(item.description)} />
                        </NumberedItem>
                      ))}
                    </NumberedList>
                  </ContentText>
                </SectionContent>
              </ContentSection>
            );
          }

          if (section.type === 'services') {
            return (
              <ContentSection key={section.id}>
                <AnimatedWords
                  as={SectionHeading}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.1 }}
                  variants={createHeadingVariants(headingAnimationComplete ? 0 : headingAnimationGateDuration)}
                >
                  {section.title}
                </AnimatedWords>
                <SectionContent>
                  <ContentText>
                    {section.categories.map((category) => (
                      <React.Fragment key={category.title}>
                        <ContentHeading dangerouslySetInnerHTML={renderHTML(category.title)} />
                        {category.paragraphs.map((paragraph, index) => (
                          <ContentParagraph
                            key={`${category.title}-paragraph-${index}`}
                            dangerouslySetInnerHTML={renderHTML(paragraph)}
                          />
                        ))}
                        <ServiceList variants={createListVariants(category.listDelay ?? 0)}>
                          {category.services.map((service) => (
                            <ServiceItem key={`${category.title}-service-${service}`}>
                              {service}
                            </ServiceItem>
                          ))}
                        </ServiceList>
                      </React.Fragment>
                    ))}
                  </ContentText>
                </SectionContent>
              </ContentSection>
            );
          }

          if (section.type === 'contact') {
            return (
              <ContentSection key={section.id}>
                <AnimatedWords
                  as={SectionHeading}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.1 }}
                  variants={createHeadingVariants(headingAnimationComplete ? 0 : headingAnimationDuration)}
                >
                  {section.title}
                </AnimatedWords>
                <SectionContent>
                  <ContactForm />
                </SectionContent>
              </ContentSection>
            );
          }

          return (
            <ContentSection key={section.id}>
              <AnimatedWords
                as={SectionHeading}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={createHeadingVariants(headingAnimationComplete ? 0 : headingAnimationDuration)}
              >
                {section.title}
              </AnimatedWords>
              <SectionContent>
                <ContentText
                  {...(section.delayUntilHero && !headingAnimationComplete
                    ? { variants: createContentDelayVariants(headingAnimationGateDuration) }
                    : {})}
                >
                  {section.contentBlocks?.map((block, index) => {
                    if (block.type === 'paragraph') {
                      return (
                        <ContentParagraph
                          key={`${section.id}-paragraph-${index}`}
                          dangerouslySetInnerHTML={renderHTML(block.html)}
                        />
                      );
                    }

                    if (block.type === 'contentHeading') {
                      return (
                        <ContentHeading
                          key={`${section.id}-heading-${index}`}
                          dangerouslySetInnerHTML={renderHTML(block.html)}
                        />
                      );
                    }

                    return null;
                  })}
                </ContentText>
              </SectionContent>
            </ContentSection>
          );
        })}
      </ContentSections>
      
      <CorporateFooter>
        <FooterText>
          © {new Date().getFullYear()} New Work City. All rights reserved.
        </FooterText>
      </CorporateFooter>
    </CorporateContainer>
  );
};

export default CorporateContent;
